import * as React from 'react'
import NumberFieldInnerTable from '../../inputs/NumberFieldInnerTable'
import {
  ContractDetailsModel,
  CustomDeduction,
  CustomDeductionType,
} from '@/modules/drivers/types/contracts'
import RateReduceCustom from '../shared/RateReduceCustom'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'

interface CustomDeductionsProps {
  data: ContractDetailsModel
  setData: (data: ContractDetailsModel) => void
}

const CustomDeductions: React.FC<CustomDeductionsProps> = ({
  data,
  setData,
}) => {
  const { t } = useLocalize()
  const customDeductions = data?.customDeductions || []

  const addNewDeduction = () => {
    const newDeduction: CustomDeduction = {
      id: Date.now().toString(),
      name: '',
      employerContribution: 0,
      employeeContribution: 0,
      deductionType: CustomDeductionType.Month,
    }
    setData({
      ...data,
      customDeductions: [...customDeductions, newDeduction],
    })
  }

  const removeDeduction = (id: string) => {
    setData({
      ...data,
      customDeductions: customDeductions.filter((item) => item.id !== id),
    })
  }

  const updateDeduction = (
    id: string,
    field: keyof CustomDeduction,
    value: any,
  ) => {
    setData({
      ...data,
      customDeductions: customDeductions.map((item) =>
        item.id === id ? { ...item, [field]: value } : item,
      ),
    })
  }

  return (
    <div className="tw-flex tw-flex-col tw-gap-3 tw-px-5 tw-pb-3 tw-pt-3">
      {customDeductions.length === 0 && (
        <div className="tw-text-content-subtle tw-text-xs tw-italic">
          {t('contract.no-deductions')}
        </div>
      )}

      {customDeductions.map((deduction, index) => (
        <div
          key={deduction.id}
          className="tw-border-border-default tw-bg-background-subtle tw-flex tw-flex-col tw-gap-2 tw-rounded tw-border tw-p-3"
        >
          <div className="tw-flex tw-items-center tw-justify-between">
            <span className="tw-text-content-default tw-text-xs tw-font-medium">
              {t('contract.deduction')} #{index + 1}
            </span>
            <button
              type="button"
              onClick={() => removeDeduction(deduction.id!)}
              className="tw-text-xs tw-text-red-600 tw-underline hover:tw-text-red-700"
            >
              {t('operation.delete')}
            </button>
          </div>

          <div className="tw-flex tw-flex-col tw-gap-1">
            <label className="tw-text-content-subtle tw-text-xs">
              {t('contract.deduction-name')}
            </label>
            <input
              type="text"
              className="tw-border-border-default tw-bg-background-default tw-w-full tw-rounded tw-border tw-px-3 tw-py-2 tw-text-sm"
              placeholder={t('contract.deduction-name-placeholder')}
              value={deduction.name}
              onChange={(e) =>
                updateDeduction(deduction.id!, 'name', e.target.value)
              }
            />
          </div>

          <div className="tw-flex tw-flex-col tw-gap-1">
            <label className="tw-text-content-subtle tw-text-xs">
              {t('contract.employer-contribution')}
            </label>
            <NumberFieldInnerTable
              innerLabel="PLN"
              placeholder="0,00"
              value={deduction.employerContribution?.toString() || ''}
              onChange={(e) =>
                updateDeduction(
                  deduction.id!,
                  'employerContribution',
                  Number(e.target.value),
                )
              }
            />
          </div>

          <div className="tw-flex tw-flex-col tw-gap-1">
            <label className="tw-text-content-subtle tw-text-xs">
              {t('contract.employee-contribution')}
            </label>
            <NumberFieldInnerTable
              innerLabel="PLN"
              placeholder="0,00"
              value={deduction.employeeContribution?.toString() || ''}
              onChange={(e) =>
                updateDeduction(
                  deduction.id!,
                  'employeeContribution',
                  Number(e.target.value),
                )
              }
            />
          </div>

          <div className="tw-flex tw-flex-col tw-gap-1">
            <label className="tw-text-content-subtle tw-text-xs">
              {t('contract.settlement-type')}
            </label>
            <select
              className="tw-border-border-default tw-bg-background-default tw-w-full tw-rounded tw-border tw-px-3 tw-py-2 tw-text-sm"
              value={deduction.deductionType ?? CustomDeductionType.Month}
              onChange={(e) =>
                updateDeduction(
                  deduction.id!,
                  'deductionType',
                  Number(e.target.value),
                )
              }
            >
              <option value={CustomDeductionType.Month}>{t('contract.monthly')}</option>
              <option value={CustomDeductionType.Day}>{t('contract.daily')}</option>
            </select>
          </div>

          {(deduction.deductionType === CustomDeductionType.Month ||
            deduction.deductionType === undefined) && (
            <RateReduceCustom
              background
              data={
                {
                  ...data,
                  reduceCustomDeductionDev: deduction.reduceCustomDeduction,
                  reduceCustomDeductionForSickLeave:
                    deduction.reduceCustomDeductionForSickLeave,
                  reduceCustomDeductionForLeave:
                    deduction.reduceCustomDeductionForLeave,
                  reduceCustomDeductionForNotPaidLeave:
                    deduction.reduceCustomDeductionForNotPaidLeave,
                } as any
              }
              setData={(newData: any) => {
                const updatedDeductions = customDeductions.map((item) =>
                  item.id === deduction.id
                    ? {
                        ...item,
                        reduceCustomDeduction: newData.reduceCustomDeductionDev,
                        reduceCustomDeductionForSickLeave:
                          newData.reduceCustomDeductionForSickLeave,
                        reduceCustomDeductionForLeave:
                          newData.reduceCustomDeductionForLeave,
                        reduceCustomDeductionForNotPaidLeave:
                          newData.reduceCustomDeductionForNotPaidLeave,
                      }
                    : item,
                )
                setData({
                  ...data,
                  customDeductions: updatedDeductions,
                })
              }}
              reduceType="additional"
              mainCheckboxProperty="reduceCustomDeduction"
              sickLeaveProperty="reduceCustomDeductionForSickLeave"
              paidLeaveProperty="reduceCustomDeductionForLeave"
              unpaidLeaveProperty="reduceCustomDeductionForNotPaidLeave"
            />
          )}
        </div>
      ))}

      <button
        type="button"
        onClick={addNewDeduction}
        className="tw-w-full tw-rounded tw-bg-blue-600 tw-px-4 tw-py-2 tw-text-sm tw-text-white tw-transition-colors hover:tw-bg-blue-700"
      >
        + {t('contract.add-deduction')}
      </button>
    </div>
  )
}

export default CustomDeductions
